import { getCookie, setCookie } from 'cookie'
import hosts from 'hosts.json'
import Cookies from 'js-cookie';

// Helper to derive API host from hostname for dynamic staging environments
function getApiHost(hostname: string): string {
  if (hosts.apiHost[hostname]) {
    return hosts.apiHost[hostname]
  }
  // For staging environments (app.staging.payhelm.com or app.stagingN.payhelm.com), derive API host
  const stagingMatch = hostname.match(/^app\.(staging\d*)\.payhelm\.com$/)
  if (stagingMatch) {
    return `https://client.${stagingMatch[1]}.payhelm.com`
  }
  // Fallback to production
  return hosts.apiHost['app.payhelm.com']
}

const cookieNames = ['name', 'paypal_user_id', 'paypal_hash_token', 'ebay_session_id', 'unique_user_id', 'app_token']

export const getLoginInfo = () => {
  return Object.assign(
    {},
    ...cookieNames.map((name) => {
      return { [name]: getCookie(name) }
    })
  )
}

export const redirectHomepage = (message, messageType, page) => {
  const targetPage = page || '/login'
  // Don't redirect if already on the target page to prevent infinite loops
  if (window.location.pathname === targetPage || window.location.pathname === '/login') {
    return
  }
  if (process.env.NODE_ENV === 'development') {
    // In development, clear cookies and redirect to login
    cookieNames.forEach((name) => {
      Cookies.remove(name)
    })
    if (message && messageType) {
      setCookie('loginMessage', message, 1000 * 60)
      setCookie('loginMessageType', messageType, 1000 * 60)
    }
    window.location.href = targetPage
  } else {
    const { hostname } = location
    cookieNames.forEach((name) => {
      Cookies.remove(name, {
        domain: '.payhelm.com',
      })
    })
    if (message && messageType) {
      setCookie('loginMessage', message, 1000 * 60)
      setCookie('loginMessageType', messageType, 1000 * 60)
    }
    window.location = getApiHost(hostname) + targetPage
  }
}

const buildLoginPath = ({ redirect = true, loginError = null } = {}) => {
  const redirectPath = window.location.pathname + window.location.search
  let queryParams = []
  if (redirect && redirectPath !== '/') {
    queryParams.push(`redirect=${encodeURIComponent(redirectPath)}`)
  }
  if (loginError) {
    queryParams.push(
      `pageError=${encodeURIComponent(
        'An error occurred during sign in. Please contact support if this issue persists.'
      )}`
    )
  }
  return '/login' + (queryParams.length ? '?' + queryParams.join('&') : '')
}

export const redirectLogin = ({ redirect = true, message = null, messageType = null, loginError = null } = {}) => {
  // Don't redirect if already on /login to prevent infinite loops
  if (window.location.pathname === '/login') {
    return
  }
  redirectHomepage(message, messageType, buildLoginPath({ redirect, loginError }))
}

/**
 * Open PayHelm login outside the current frame (WP admin embeds, etc.).
 * Papaya /login is X-Frame-Options SAMEORIGIN and Google OAuth refuses iframes,
 * so navigating the iframe itself to /login always fails.
 */
export const openLoginTopLevel = ({ redirect = true, message = null, messageType = null, loginError = null } = {}) => {
  if (window.location.pathname === '/login') {
    return
  }
  const page = buildLoginPath({ redirect, loginError })
  // Match redirectHomepage: drop session cookies so "sign in with a different
  // account" cannot silently reuse the previous app_token.
  if (process.env.NODE_ENV === 'development') {
    cookieNames.forEach((name) => {
      Cookies.remove(name)
    })
  } else {
    cookieNames.forEach((name) => {
      Cookies.remove(name, {
        domain: '.payhelm.com',
      })
    })
  }
  if (message && messageType) {
    setCookie('loginMessage', message, 1000 * 60)
    setCookie('loginMessageType', messageType, 1000 * 60)
  }
  const url =
    process.env.NODE_ENV === 'development'
      ? page
      : getApiHost(location.hostname) + page
  // `_top` breaks out of the plugin iframe into the parent browsing context.
  window.open(url, '_top')
}
